import { useContext, useCallback, useMemo, useEffect } from "react"
import ListItemDropDown from "components/list-item-dropdown"
import PeriodDisplay from "connected-components/dashboard/components/period-display"
import GraphTemplateSelector from "connected-components/graph/components/template-selector"
import { MODEL_METADATA_SHAPE } from "connected-components/asset-view/prop-types"
import { INSTRUMENTS_WITH_METADATA_SHAPE } from "connected-components/asset-view/prop-types"
import { PERFORMANCE_CONTEXT } from "./context"
import { GRAPH_CONTEXT_SHAPE } from "../../prop-types"

export default function GraphHeader({ modelMetadata, context }) {
  const { selectedCategory, setSelectedCategory } = useContext(PERFORMANCE_CONTEXT)

  // Generate category options directly from model metadata
  const categoryOptions = useMemo(() => {
    // These basic categories are fundamental analytical concepts in performance analysis
    // "total" = overall portfolio performance, "factors" = all factor performance combined
    // They're always available regardless of model metadata
    const basicCategories = [
      { id: "total", primaryText: "Total" },
      { id: "factors", primaryText: "Factors" },
    ]

    if (!modelMetadata?.categories || !modelMetadata?.factors) {
      return basicCategories
    }

    // Filter out single-item categories by checking how many factors belong to each category
    const categoryFactorCounts = modelMetadata.factors.reduce((counts, factor) => {
      const category = factor.category
      if (category) {
        counts[category] = (counts[category] || 0) + 1
      }
      return counts
    }, {})

    // Add model-specific categories that have more than one factor
    const modelCategories = modelMetadata.categories.reduce((acc, category) => {
      if (categoryFactorCounts[category.id] > 1) {
        acc.push({
          id: category.id,
          primaryText: category.label,
        })
      }
      return acc
    }, [])

    return [...basicCategories, ...modelCategories]
  }, [modelMetadata])

  useEffect(() => {
    if (categoryOptions.length > 0 && !categoryOptions.some(({ id }) => id === selectedCategory)) {
      setSelectedCategory(categoryOptions[0].id)
    }
  }, [categoryOptions, selectedCategory, setSelectedCategory])

  const handleCategoryChange = useCallback((category) => {
    setSelectedCategory(category)
  }, [setSelectedCategory])

  return (
    <>
      <GraphTemplateSelector context={context} />
      <PeriodDisplay context={context} />
      <ListItemDropDown
        label="Category"
        value={selectedCategory}
        setValue={handleCategoryChange}
        options={categoryOptions}
        alignMenu="left"
        disablePortal
      />
    </>
  )
}

GraphHeader.propTypes = {
  modelMetadata: MODEL_METADATA_SHAPE.isRequired,
  context: GRAPH_CONTEXT_SHAPE.isRequired,
  instrumentsWithMetadata: INSTRUMENTS_WITH_METADATA_SHAPE.isRequired,
}
